// Zustand によるグローバル状態。
// 状態は最小限。fetch ロジックは api.ts、副作用は App.tsx で組む。

import { create } from 'zustand';
import type { StationMeta, TideResponse } from '@tideprecision/shared';
import { setSetting } from './idb.js';

// 起動時に復元する「最後に見ていた地点」のキー
export const LAST_STATION_KEY = 'lastStation';

export interface AppState {
  selectedStation: StationMeta | null;
  date: string; // YYYY-MM-DD (JST)
  tideData: TideResponse | null;
  isOffline: boolean;
  isLoading: boolean;
  error: string | null;
  favorites: number[]; // station_id 配列
  // 「同じ地点・同じ日付」のまま強制再フェッチを促すためのトークン。更新ボタンから ++ する。
  refreshKey: number;

  setSelectedStation(s: StationMeta | null): void;
  setDate(d: string): void;
  setTideData(d: TideResponse | null): void;
  setIsOffline(v: boolean): void;
  setIsLoading(v: boolean): void;
  setError(e: string | null): void;
  addFavorite(id: number): void;
  removeFavorite(id: number): void;
  triggerRefresh(): void;
}

const FAVORITES_KEY = 'tide.favorites';

function loadFavorites(): number[] {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY) ?? '[]') as number[];
  } catch {
    return [];
  }
}

function saveFavorites(ids: number[]): void {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

export const useStore = create<AppState>((set) => ({
  selectedStation: null,
  date: todayJst(),
  tideData: null,
  isOffline: !navigator.onLine,
  isLoading: false,
  error: null,
  favorites: loadFavorites(),
  refreshKey: 0,

  setSelectedStation: (s) => {
    set({ selectedStation: s });
    // 次回起動時の初期表示に使うため IndexedDB に永続化（fire-and-forget）
    if (s) void setSetting(LAST_STATION_KEY, s);
  },
  setDate: (d) => set({ date: d }),
  setTideData: (d) => set({ tideData: d }),
  setIsOffline: (v) => set({ isOffline: v }),
  setIsLoading: (v) => set({ isLoading: v }),
  setError: (e) => set({ error: e }),
  addFavorite: (id) =>
    set((st) => {
      if (st.favorites.includes(id)) return st;
      const next = [...st.favorites, id];
      saveFavorites(next);
      return { favorites: next };
    }),
  removeFavorite: (id) =>
    set((st) => {
      const next = st.favorites.filter((x) => x !== id);
      saveFavorites(next);
      return { favorites: next };
    }),
  triggerRefresh: () => set((st) => ({ refreshKey: st.refreshKey + 1 })),
}));

export function todayJst(): string {
  const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return now.toISOString().slice(0, 10);
}

export function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
